import React, { useState } from "react";
import { styled, Box } from "@mui/material";
import {
  Avatar,
  Popover,
  MenuItem,
  Divider,
  Typography,
  Skeleton,
} from "@mui/material";
import useAuth from "@/hooks/use-auth";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { logoutUser } from "@/services/auth.service";
import NotificationBar from "../NotificationBar/NotificationBar";
import ThemeToggle from "../appTheme/theme-toggle";
import { formatAccessType } from "@/utils/format-access-type";
import { useAdminUser } from "@/hooks/use-admin-user";
import { getInitials } from "@/utils/get-initals";

const NavbarRoot = styled("div")(({ theme }) => ({
  top: 0,
  zIndex: 96,
  transition: "all 0.3s ease",
  height: 64,
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const NavbarContainer = styled(Box)(({ theme }) => ({
  paddingLeft: 20,
  paddingRight: 20,
  height: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  background: theme.palette.background.default,
}));

const RightSection = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  gap: 8,
}));

const AvatarButton = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  gap: 8,
  cursor: "pointer",
  borderRadius: 24,
  padding: 4,
}));

const StyledMenuItem = styled(MenuItem, {
  shouldForwardProp: (prop) => prop !== "danger",
})(({ theme, danger }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.5),
  minWidth: 180,
  fontSize: "0.875rem",
  padding: theme.spacing(1, 2),
  color: danger ? theme.palette.error.main : "inherit",
  "&:hover": {
    backgroundColor: danger
      ? `${theme.palette.error.main}10`
      : theme.palette.action.hover,
  },
}));

const PopoverContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(0.5),
}));

const NavbarUserSkeleton = () => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1, p: 0.5 }}>
    <Skeleton variant="circular" width={36} height={36} />
    <Skeleton variant="text" width={80} height={20} />
  </Box>
);

const Navbar = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [anchorEl, setAnchorEl] = useState(null);
  const isOpen = Boolean(anchorEl);

  const { data: adminUser, isLoading } = useAdminUser();

  const firstName = adminUser?.firstName ?? "";
  const lastName = adminUser?.surname ?? "";
  const email = adminUser?.email ?? "";
  const initials = getInitials(firstName, lastName);

  const logoutMutation = useMutation({
    mutationFn: logoutUser,
    onSuccess: async () => {
      await queryClient.cancelQueries();
      queryClient.clear();
      logout();
      navigate("/session/login", { replace: true });
    },
    onError: () => {
      queryClient.clear();
      logout();
      navigate("/session/login", { replace: true });
    },
  });

  const handleOpen = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleNavigate = (path) => {
    handleClose();
    navigate(path);
  };

  return (
    <NavbarRoot>
      <NavbarContainer>
        <Box />

        <RightSection>
          <ThemeToggle />
          <NotificationBar />

          {isLoading ? (
            <NavbarUserSkeleton />
          ) : (
            <AvatarButton onClick={handleOpen}>
              <Avatar
                sx={{
                  width: 36,
                  height: 36,
                  cursor: "pointer",
                  bgcolor: "primary.secondary",
                  fontSize: "0.85rem",
                }}
              >
                {initials}
              </Avatar>
              <Typography variant="body2" fontWeight={500}>
                {formatAccessType(firstName)}
              </Typography>
            </AvatarButton>
          )}
        </RightSection>
      </NavbarContainer>

      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            elevation: 2,
            sx: { borderRadius: "8px", mt: 0.5 },
          },
        }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="body2" fontWeight={600}>
            {firstName ? `${firstName} ${lastName}` : "Admin"}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {email}
          </Typography>
        </Box>

        <Divider />

        <PopoverContent>
          <StyledMenuItem onClick={() => handleNavigate("/profile")}>
            Profile
          </StyledMenuItem>

          <StyledMenuItem onClick={() => handleNavigate("/settings")}>
            Settings
          </StyledMenuItem>

          <Divider sx={{ my: 0.5 }} />

          <StyledMenuItem
            danger={1}
            onClick={() => {
              handleClose();
              logoutMutation.mutate();
            }}
            disabled={logoutMutation.isPending}
          >
            {logoutMutation.isPending ? "Logging out..." : "Logout"}
          </StyledMenuItem>
        </PopoverContent>
      </Popover>
    </NavbarRoot>
  );
};

export default Navbar;
